import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axios/axiosInstance';

const EMPTY_FORM = {
    name: '', description: '',
    companyMonthlyPrice: '', employeeMonthlyPrice: '', contractorMonthlyPrice: '',
    maxCompanies: -1, maxEmployees: -1, maxUsers: -1,
    trialDays: 7, isActive: true,
};

const Field = ({ label, hint, children }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>{label}</label>
        {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{hint}</div>}
        {children}
    </div>
);

const Input = ({ value, onChange, placeholder, type = 'text', prefix }) => (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 7, overflow: 'hidden' }}>
        {prefix && <span style={{ padding: '0 10px', background: '#f9fafb', color: '#6b7280', fontSize: 13, borderRight: '1px solid #d1d5db' }}>{prefix}</span>}
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            style={{ flex: 1, padding: '8px 10px', border: 'none', fontSize: 13, outline: 'none' }}
        />
    </div>
);

const Spinner = () => (
    <div style={{ width: 16, height: 16, border: '2px solid #E7730A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
);

const PlanManagement = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/plans/all');
            setPlans(res.data);
        } catch { showToast('Failed to load plans.', 'error'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchPlans(); }, []);

    const openPlan = (plan) => {
        setIsNew(false);
        setSelected(plan);
        setForm({
            name:                   plan.name                   || '',
            description:            plan.description            || '',
            companyMonthlyPrice:    plan.companyMonthlyPrice    ?? '',
            employeeMonthlyPrice:   plan.employeeMonthlyPrice   ?? '',
            contractorMonthlyPrice: plan.contractorMonthlyPrice ?? '',
            maxCompanies:           plan.maxCompanies           ?? -1,
            maxEmployees:           plan.maxEmployees           ?? -1,
            maxUsers:               plan.maxUsers               ?? -1,
            trialDays:              plan.trialDays              ?? 7,
            isActive:               plan.isActive               ?? true,
        });
    };

    const openNew = () => { setIsNew(true); setSelected({}); setForm(EMPTY_FORM); };
    const closePanel = () => { setSelected(null); setIsNew(false); };
    const setField = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

    const toNum = (v) => v === '' ? null : Number(v);

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Plan name is required.', 'error'); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name, description: form.description || null,
                companyMonthlyPrice:    toNum(form.companyMonthlyPrice),
                employeeMonthlyPrice:   toNum(form.employeeMonthlyPrice),
                contractorMonthlyPrice: toNum(form.contractorMonthlyPrice),
                maxCompanies: Number(form.maxCompanies),
                maxEmployees: Number(form.maxEmployees),
                maxUsers:     Number(form.maxUsers),
                trialDays:    Number(form.trialDays),
                isActive:     form.isActive,
            };
            if (isNew) {
                const res = await axiosInstance.post('/plans', payload);
                setPlans(p => [...p, res.data]);
                showToast(`Plan "${res.data.name}" created.`);
                closePanel();
            } else {
                const res = await axiosInstance.put(`/plans/${selected.id}`, payload);
                setPlans(p => p.map(x => x.id === selected.id ? { ...x, ...res.data } : x));
                showToast('Plan updated.');
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save plan.', 'error');
        } finally { setSaving(false); }
    };

    const handleDelete = async (plan) => {
        if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
        try {
            await axiosInstance.delete(`/plans/${plan.id}`);
            setPlans(p => p.filter(x => x.id !== plan.id));
            if (selected?.id === plan.id) closePanel();
            showToast(`Plan "${plan.name}" deleted.`);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete plan.', 'error');
        }
    };

    const monthlyTotal = (plan) => {
        const c = plan.companyMonthlyPrice ?? 0;
        const e = plan.employeeMonthlyPrice ?? 0;
        const x = plan.contractorMonthlyPrice ?? 0;
        return { c, e, x };
    };

    return (
        <div>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a3a5c', margin: 0 }}>Package & Pricing Management</h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
                        Configure packages, usage-based pricing, and trial settings. All prices are per entity per month.
                    </p>
                </div>
                <button onClick={openNew} style={{
                    background: '#E7730A', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>+ New Package</button>
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                            {plans.map(plan => {
                                const { c, e, x } = monthlyTotal(plan);
                                const isSelected = selected?.id === plan.id;
                                return (
                                    <div key={plan.id} onClick={() => openPlan(plan)} style={{
                                        background: '#fff', borderRadius: 12, cursor: 'pointer',
                                        border: `2px solid ${isSelected ? '#E7730A' : '#e2e8f0'}`,
                                        padding: '18px 20px', transition: 'all 0.15s',
                                        opacity: plan.isActive ? 1 : 0.6,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{plan.name}</div>
                                                {plan.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{plan.description}</div>}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }} onClick={ev => ev.stopPropagation()}>
                                                {!plan.isActive && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>Inactive</span>}
                                                <button onClick={() => handleDelete(plan)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16 }}
                                                    onMouseEnter={ev => ev.currentTarget.style.color = '#dc2626'}
                                                    onMouseLeave={ev => ev.currentTarget.style.color = '#d1d5db'}>×</button>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                                            {[
                                                { label: 'Per Company', val: c },
                                                { label: 'Per Employee', val: e },
                                                { label: 'Per Contractor', val: x },
                                            ].map(({ label, val }) => (
                                                <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                                                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{label}</div>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#E7730A', marginTop: 2 }}>
                                                        {val > 0 ? `$${val}` : '—'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#64748b' }}>
                                            <span>🏢 {plan.maxCompanies < 0 ? '∞' : plan.maxCompanies} cos</span>
                                            <span>👤 {plan.maxEmployees < 0 ? '∞' : plan.maxEmployees} emps</span>
                                            <span>⏱ {plan.trialDays}d trial</span>
                                        </div>
                                    </div>
                                );
                            })}
                            {plans.length === 0 && (
                                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                                    No packages yet. Create one to get started.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selected !== null && (
                    <div style={{ width: 340, flexShrink: 0, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1a3a5c' }}>{isNew ? 'New Package' : form.name}</span>
                            <button onClick={closePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20 }}>×</button>
                        </div>

                        <div style={{ padding: '16px 18px', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Package Info</div>
                            <Field label="Package Name *"><Input value={form.name} onChange={setField('name')} placeholder="Accountant Pro" /></Field>
                            <Field label="Description"><Input value={form.description} onChange={setField('description')} placeholder="For accountants managing multiple companies" /></Field>

                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>Usage-Based Pricing</div>
                            <div style={{ background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#92400e' }}>
                                💡 Set price per entity per month. Leave blank for no charge on that entity type.
                            </div>
                            <Field label="Per Company / Month"><Input value={form.companyMonthlyPrice} onChange={setField('companyMonthlyPrice')} prefix="$" type="number" placeholder="0.00" /></Field>
                            <Field label="Per Employee / Month"><Input value={form.employeeMonthlyPrice} onChange={setField('employeeMonthlyPrice')} prefix="$" type="number" placeholder="0.00" /></Field>
                            <Field label="Per Contractor / Month"><Input value={form.contractorMonthlyPrice} onChange={setField('contractorMonthlyPrice')} prefix="$" type="number" placeholder="0.00" /></Field>

                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>Limits & Trial</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <Field label="Max Companies" hint="-1 = unlimited"><Input value={form.maxCompanies} onChange={setField('maxCompanies')} type="number" /></Field>
                                <Field label="Max Employees" hint="-1 = unlimited"><Input value={form.maxEmployees} onChange={setField('maxEmployees')} type="number" /></Field>
                                <Field label="Max Users" hint="-1 = unlimited"><Input value={form.maxUsers} onChange={setField('maxUsers')} type="number" /></Field>
                                <Field label="Trial Days"><Input value={form.trialDays} onChange={setField('trialDays')} type="number" /></Field>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                <input type="checkbox" checked={form.isActive} onChange={e => setField('isActive')(e.target.checked)}
                                    style={{ width: 15, height: 15, accentColor: '#E7730A', cursor: 'pointer' }} />
                                <label style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>Active (visible to new signups)</label>
                            </div>
                        </div>

                        <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                            <button onClick={handleSave} disabled={saving} style={{
                                flex: 1, background: '#E7730A', color: '#fff', border: 'none',
                                borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 600,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}>
                                {saving ? <><Spinner /> Saving…</> : isNew ? 'Create Package' : 'Save Changes'}
                            </button>
                            <button onClick={closePanel} style={{ padding: '9px 14px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569' }}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanManagement;
